import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Buildings, FilesIcon, FloppyDisk, ChartLine, MagnifyingGlass, Prohibit, Trash, ActivityIcon } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const fmtDate = (d) => new Date(d).toLocaleString();

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activity, setActivity] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/admin"); return; }
    if (user.role !== "admin") { navigate("/"); toast.error("Admin access only"); return; }
    fetchStats(); fetchUsers(); fetchTeams(); fetchActivity();
     
  }, [user, authLoading]);

  const fetchStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(formatApiError(e)));
  const fetchUsers = () => api.get("/admin/users", { params: { q: q || undefined } }).then(({ data }) => setUsers(data.items || []));
  const fetchTeams = () => api.get("/admin/teams", { params: { q: q || undefined } }).then(({ data }) => setTeams(data.items || []));
  const fetchActivity = () => api.get("/admin/activity").then(({ data }) => setActivity(data.items || []));

  useEffect(() => {
    if (tab === "users") fetchUsers();
    if (tab === "teams") fetchTeams();
     
  }, [q, tab]);

  const suspendTeam = async (tid) => {
    if (!window.confirm(`Suspend team ${tid}?`)) return;
    try { await api.post(`/admin/teams/${tid}/suspend`); toast.success("Suspended"); fetchTeams(); fetchStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const unsuspendTeam = async (tid) => {
    try { await api.post(`/admin/teams/${tid}/unsuspend`); toast.success("Reinstated"); fetchTeams(); fetchStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteTeam = async (tid) => {
    if (!window.confirm(`Permanently delete team ${tid}? This is reversible from the DB but not the UI.`)) return;
    try { await api.delete(`/admin/teams/${tid}`); toast.success("Deleted"); fetchTeams(); fetchStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const suspendUser = async (uid) => {
    if (!window.confirm("Suspend this user?")) return;
    try { await api.post(`/admin/users/${uid}/suspend`); toast.success("User suspended"); fetchUsers(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const unsuspendUser = async (uid) => {
    try { await api.post(`/admin/users/${uid}/unsuspend`); toast.success("User restored"); fetchUsers(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (authLoading || !user) return <PageShell title="Loading…" />;

  return (
    <PageShell eyebrow="Admin" title="Super admin dashboard." subtitle="Platform-wide visibility and controls. Handle suspicious activity, suspend teams, audit logs.">
      <div className="flex gap-1 border-b border-black/15 mb-6">
        {[["overview", "Overview"], ["users", "Users"], ["teams", "Teams"], ["activity", "Activity log"]].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${tab === k ? "border-black text-black" : "border-transparent text-black/55 hover:text-black"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          <Stat icon={<Users size={20}/>} label="Total users" value={stats.total_users} accent="#FF3366"/>
          <Stat icon={<Buildings size={20}/>} label="Total teams" value={stats.total_teams} accent="#10B981"/>
          <Stat icon={<FilesIcon size={20}/>} label="Team files" value={stats.total_team_files} accent="#0A0A0B"/>
          <Stat icon={<FloppyDisk size={20}/>} label="Storage used" value={fmtBytes(stats.total_storage_used)} accent="#F97316"/>
          <Stat icon={<ChartLine size={20}/>} label="Transfers" value={stats.total_transfers} accent="#FF3366"/>
          <Stat icon={<ActivityIcon size={20}/>} label="Active today" value={stats.active_users_today} accent="#10B981"/>
          <Stat icon={<Prohibit size={20}/>} label="Suspended teams" value={stats.suspended_teams} accent="#0A0A0B"/>
        </div>
      )}

      {(tab === "users" || tab === "teams") && (
        <div className="flex items-center gap-2 border border-black/20 px-3 py-2 bg-white max-w-md mb-4">
          <MagnifyingGlass size={14}/>
          <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab}…`} className="bg-transparent outline-none text-sm w-full"/>
        </div>
      )}

      {tab === "users" && (
        <div className="overflow-x-auto border border-black" data-testid="admin-users-table">
          <table className="w-full text-sm">
            <thead className="bg-black/5"><tr className="text-left">
              <th className="px-3 py-2 small-caps">Name</th>
              <th className="px-3 py-2 small-caps">Email</th>
              <th className="px-3 py-2 small-caps">Mobile</th>
              <th className="px-3 py-2 small-caps">Role</th>
              <th className="px-3 py-2 small-caps">Plan</th>
              <th className="px-3 py-2 small-caps">Joined</th>
              <th className="px-3 py-2 small-caps text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-black/10">
              {users.map((u) => (
                <tr key={u.user_id} className="hover:bg-black/[0.03]" data-testid={`admin-user-${u.user_id}`}>
                  <td className="px-3 py-2.5 font-medium">{u.name}</td>
                  <td className="px-3 py-2.5 truncate max-w-[220px]">{u.email}</td>
                  <td className="px-3 py-2.5 font-mono">{u.mobile || "—"}</td>
                  <td className="px-3 py-2.5"><span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${u.role === "admin" ? "bg-[#FF3366] text-white" : "bg-black/5 text-black"}`}>{u.role}</span></td>
                  <td className="px-3 py-2.5"><span className="text-[10px] font-mono uppercase">{u.plan}</span></td>
                  <td className="px-3 py-2.5 font-mono text-xs text-black/55">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-2.5 text-right">
                    {u.status === "suspended"
                      ? <button onClick={() => unsuspendUser(u.user_id)} className="text-xs uppercase tracking-widest text-[#10B981]" data-testid={`unsuspend-user-${u.user_id}`}>Restore</button>
                      : <button onClick={() => suspendUser(u.user_id)} className="text-xs uppercase tracking-widest text-black/55 hover:text-[#FF3366]" data-testid={`suspend-user-${u.user_id}`}>Suspend</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "teams" && (
        <div className="overflow-x-auto border border-black" data-testid="admin-teams-table">
          <table className="w-full text-sm">
            <thead className="bg-black/5"><tr className="text-left">
              <th className="px-3 py-2 small-caps">Name</th>
              <th className="px-3 py-2 small-caps">ID</th>
              <th className="px-3 py-2 small-caps">Members</th>
              <th className="px-3 py-2 small-caps">Storage</th>
              <th className="px-3 py-2 small-caps">Status</th>
              <th className="px-3 py-2 small-caps">Created</th>
              <th className="px-3 py-2 small-caps text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-black/10">
              {teams.map((t) => (
                <tr key={t.team_id} className="hover:bg-black/[0.03]" data-testid={`admin-team-${t.team_id}`}>
                  <td className="px-3 py-2.5 font-medium">{t.name}</td>
                  <td className="px-3 py-2.5 font-mono">{t.team_id}</td>
                  <td className="px-3 py-2.5">{t.member_count}/10</td>
                  <td className="px-3 py-2.5 font-mono">{fmtBytes(t.storage_used)}</td>
                  <td className="px-3 py-2.5"><span className={`text-[10px] font-mono uppercase ${t.status === "suspended" ? "text-[#FF3366]" : "text-[#10B981]"}`}>{t.status}</span></td>
                  <td className="px-3 py-2.5 font-mono text-xs text-black/55">{fmtDate(t.created_at)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      {t.status === "suspended"
                        ? <button onClick={() => unsuspendTeam(t.team_id)} className="px-2 py-1 text-[10px] uppercase tracking-widest border border-[#10B981] text-[#10B981]" data-testid={`unsuspend-${t.team_id}`}>Restore</button>
                        : <button onClick={() => suspendTeam(t.team_id)} className="px-2 py-1 text-[10px] uppercase tracking-widest border border-black/20" data-testid={`suspend-${t.team_id}`}>Suspend</button>}
                      <button onClick={() => deleteTeam(t.team_id)} className="px-2 py-1 text-[10px] uppercase tracking-widest border border-black/20 hover:border-[#FF3366] hover:text-[#FF3366]" data-testid={`delete-${t.team_id}`}><Trash size={11} className="inline"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "activity" && (
        <div className="brut-card p-6" data-testid="admin-activity">
          {activity.length === 0 ? <div className="text-black/55">No activity yet.</div> : (
            <ul className="divide-y divide-black/10">
              {activity.map((a) => (
                <li key={a.id} className="py-2.5 flex items-start justify-between gap-3">
                  <div className="text-sm">
                    <span className="font-mono text-[#FF3366]">{a.action}</span>
                    <span className="text-black/55 ml-2">by {a.actor_user_id || "anon"}</span>
                    <span className="text-black/55 ml-2">on {a.target_type}/{a.target_id}</span>
                  </div>
                  <div className="text-[11px] font-mono text-black/55 whitespace-nowrap">{fmtDate(a.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </PageShell>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className="brut-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="small-caps">{label}</div>
        <div style={{ color: accent }}>{icon}</div>
      </div>
      <div className="text-3xl font-black tracking-tighter">{value ?? "—"}</div>
    </div>
  );
}
